import API_ENDPOINTS from '../config/api'
import {
  adaptPackageCoreferenceChainsToDetectorOutput,
  PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT,
} from '../lib/characterIndexCoreferenceOutputAdapter'
import {
  adaptPackageCharacterEvidenceToDetectorOutput,
  PACKAGE_CHARACTER_EVIDENCE_OUTPUT_CONTRACT,
} from '../lib/characterIndexPreparedOutputAdapter'
import {
  adaptPackageCoreferenceChainV2ToDetectorOutput,
  adaptPackageFocalizationCharacterizationToDetectorOutput,
  PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT,
  PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT,
} from '../lib/characterIndexLayeredOutputAdapters'
import type {
  CharacterEntity,
  CharacterLayerDetectorAdapter,
  CharacterLayerDetectorOutput,
  CharacterLayerDetectionRequest,
  CharacterLayerOrigin,
  CharacterLayerStatus,
  CharacterOccurrence,
} from '../types/characterLayerTypes'
import type {
  CoreferenceChainV2FigureKind as CharacterIndexFigureKind,
  PreparedSingleWitnessCoreferenceChainV2WindowCall,
  PreparedSingleWitnessCoreferenceChainWindowCall,
} from '@variorum/character-index'

type PreparedCoreferenceWindowCall =
  | PreparedSingleWitnessCoreferenceChainWindowCall
  | PreparedSingleWitnessCoreferenceChainV2WindowCall

interface WorkbenchCharacterIndexResult {
  chapter_id: string
  book_id?: string
  mode: 'detect-character-index'
  characters: CharacterLayerDetectorOutput['characters']
  occurrences: CharacterLayerDetectorOutput['occurrences']
  notes: CharacterLayerDetectorOutput['notes']
  diagnostics: Record<string, any>
}

const parseErrorDetail = async (response: Response, fallback: string): Promise<string> => {
  const detail = await response.text().catch(() => '')
  if (!detail) {
    return fallback
  }
  try {
    const payload = JSON.parse(detail) as { detail?: string | { message?: string } }
    if (typeof payload.detail === 'string') {
      return payload.detail
    }
    if (payload.detail && typeof payload.detail === 'object' && typeof payload.detail.message === 'string') {
      return payload.detail.message
    }
    return fallback
  } catch {
    return detail
  }
}

const nowIso = (): string => new Date().toISOString()

const isRecord = (value: unknown): value is Record<string, any> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
)

const getErrorMessage = (error: unknown): string => (
  error instanceof Error ? error.message : String(error || 'Unknown error')
)

const createCanceledError = (): Error => {
  const error = new Error('Character index run canceled.')
  error.name = 'AbortError'
  return error
}

const throwIfCanceled = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw createCanceledError()
  }
}

interface FocusedCharacterTarget {
  character_id: string
  display_name: string
  aliases?: string[]
}

const normalizeText = (value: unknown): string => (
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').toLowerCase() : ''
)

const uniqueStrings = (values: readonly unknown[]): string[] => (
  Array.from(new Set(values.map(normalizeText).filter(Boolean)))
)

const getFocusedCharacterTarget = (
  metadata: Record<string, any> | undefined,
): FocusedCharacterTarget | null => {
  const value = metadata?.focused_character
  if (!isRecord(value)) {
    return null
  }
  const characterId = typeof value.character_id === 'string' ? value.character_id.trim() : ''
  const displayName = typeof value.display_name === 'string' ? value.display_name.trim() : ''
  if (!characterId || !displayName) {
    return null
  }
  return {
    character_id: characterId,
    display_name: displayName,
    aliases: Array.isArray(value.aliases)
      ? value.aliases.filter((alias): alias is string => typeof alias === 'string')
      : [],
  }
}

const characterMatchesFocusedTarget = (
  character: CharacterEntity,
  targetNames: readonly string[],
): boolean => {
  const characterNames = uniqueStrings([
    character.display_name,
    ...(character.aliases || []),
    character.metadata?.chain_anchor,
    character.metadata?.transliteration,
  ])
  return characterNames.some(name => targetNames.includes(name))
}

const applyFocusedCharacterTarget = (
  output: CharacterLayerDetectorOutput,
  target: FocusedCharacterTarget | null,
): CharacterLayerDetectorOutput => {
  if (!target) {
    return output
  }
  const targetNames = uniqueStrings([target.display_name, ...(target.aliases || [])])
  const matchingCharacterIds = new Set<string>()
  output.characters.forEach(character => {
    if (characterMatchesFocusedTarget(character, targetNames)) {
      matchingCharacterIds.add(character.character_id)
    }
  })
  if (matchingCharacterIds.size === 0 && output.characters.length === 1) {
    matchingCharacterIds.add(output.characters[0].character_id)
  }
  const focusedOccurrences: CharacterOccurrence[] = output.occurrences
    .filter(occurrence => matchingCharacterIds.has(occurrence.character_id) || output.characters.length === 0)
    .map(occurrence => ({
      ...occurrence,
      character_id: target.character_id,
      metadata: {
        ...(occurrence.metadata || {}),
        focused_character_id: target.character_id,
        focused_character_name: target.display_name,
        source_character_id: occurrence.character_id,
      },
    }))
  return {
    ...output,
    characters: [],
    occurrences: focusedOccurrences,
    metadata: {
      ...(output.metadata || {}),
      focused_character_id: target.character_id,
      focused_character_name: target.display_name,
      focused_candidate_character_count: output.characters.length,
      focused_retained_occurrence_count: focusedOccurrences.length,
    },
  }
}

const isCoreferenceWindowCall = (value: unknown): value is PreparedCoreferenceWindowCall => {
  if (!isRecord(value)) {
    return false
  }
  return typeof value.promptText === 'string' &&
    typeof value.promptVersion === 'string' &&
    typeof value.sourceText === 'string' &&
    Array.isArray(value.sourceMap) &&
    isRecord(value.scope) &&
    isRecord(value.window) &&
    typeof value.window.windowId === 'string'
}

const getCoreferenceWindowCalls = (
  metadata: Record<string, any> | undefined,
): PreparedCoreferenceWindowCall[] => {
  const value = metadata?.coreference_window_calls
  return Array.isArray(value) ? value.filter(isCoreferenceWindowCall) : []
}

const getRowsForWindowCall = (
  request: CharacterLayerDetectionRequest,
  call: PreparedCoreferenceWindowCall,
): CharacterLayerDetectionRequest['rows'] => {
  const rowIds = new Set(call.scope.selectedRowIds)
  const rows = request.rows.filter(row => rowIds.has(row.rowId))
  return rows.length > 0 ? rows : request.rows
}

const buildCoreferenceWindowRequest = (
  request: CharacterLayerDetectionRequest,
  call: PreparedCoreferenceWindowCall,
  outputContract: string,
): CharacterLayerDetectionRequest => {
  const extractionScope: Record<string, any> = isRecord(request.metadata?.extraction_scope)
    ? request.metadata?.extraction_scope || {}
    : {}
  return {
    ...request,
    rows: getRowsForWindowCall(request, call),
    metadata: {
      ...(request.metadata || {}),
      prompt_text: call.promptText,
      prompt_version: call.promptVersion,
      output_contract: outputContract,
      prepared_extraction_call: call,
      extraction_scope: {
        ...extractionScope,
        ...call.scope,
        unit: extractionScope.unit || call.window.sourceScope.kind,
        windowed: true,
        window: call.window,
        source_scope: call.window.sourceScope,
        source_row_count: call.scope.selectedRowIds.length,
        source_text_length: call.sourceText.length,
      },
    },
  }
}

const mergeCoreferenceWindowResults = ({
  results,
  failures,
  windowCalls,
  outputContract,
}: {
  results: Array<{
    call: PreparedCoreferenceWindowCall
    result: WorkbenchCharacterIndexResult
  }>
  failures: Array<Record<string, any>>
  windowCalls: PreparedCoreferenceWindowCall[]
  outputContract: string
}): WorkbenchCharacterIndexResult => {
  const first = results[0]?.result
  const rawWindows = results.map(({ call, result }) => ({
    window: call.window,
    raw_extraction: result.diagnostics?.raw_extraction,
  }))
  return {
    chapter_id: first?.chapter_id || '',
    ...(first?.book_id ? { book_id: first.book_id } : {}),
    mode: 'detect-character-index',
    characters: results.flatMap(({ result }) => result.characters),
    occurrences: results.flatMap(({ result }) => result.occurrences),
    notes: results.flatMap(({ result }) => result.notes || []),
    diagnostics: {
      output_contract: outputContract,
      windowed: true,
      window_count: windowCalls.length,
      successful_window_count: results.length,
      failed_window_count: failures.length,
      windows: results.map(({ call, result }) => ({
        window: call.window,
        diagnostics: result.diagnostics,
      })),
      window_failures: failures,
      raw_extraction: {
        windows: rawWindows,
      },
    },
  }
}

export const requestWorkbenchCharacterIndexDetection = async (
  request: CharacterLayerDetectionRequest,
): Promise<WorkbenchCharacterIndexResult> => {
  const response = await fetch(API_ENDPOINTS.workbenchCharacterIndex.detect, {
    method: 'POST',
    signal: request.signal,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      chapter_id: request.chapterId,
      book_id: request.bookId,
      mode: 'detect-character-index',
      rows: request.rows.map(row => ({
        witness_id: row.witnessId,
        witness_siglum: row.witnessSigla,
        row_id: row.rowId,
        ...(row.rowLabel ? { row_label: row.rowLabel } : {}),
        text: row.text,
      })),
      existing_characters: (request.existingLayer?.characters || []).map(character => ({
        character_id: character.character_id,
        display_name: character.display_name,
        aliases: character.aliases || [],
        status: character.status,
        origin: character.origin,
        ...(character.notes ? { notes: character.notes } : {}),
      })),
      ...(request.metadata?.model_id ? { model_id: request.metadata.model_id } : {}),
      ...(typeof request.metadata?.temperature === 'number' && Number.isFinite(request.metadata.temperature)
        ? { temperature: request.metadata.temperature }
        : {}),
      ...(typeof request.metadata?.thinking_level === 'string' && request.metadata.thinking_level.trim()
        ? { thinking_level: request.metadata.thinking_level }
        : {}),
      ...(typeof request.metadata?.prompt_text === 'string' && request.metadata.prompt_text.trim()
        ? { prompt_text: request.metadata.prompt_text }
        : {}),
      ...(typeof request.metadata?.prompt_version === 'string' && request.metadata.prompt_version.trim()
        ? { prompt_version: request.metadata.prompt_version }
        : {}),
      ...(typeof request.metadata?.output_contract === 'string' && request.metadata.output_contract.trim()
        ? { output_contract: request.metadata.output_contract }
        : {}),
      ...(typeof request.metadata?.refinement_source_text === 'string' && request.metadata.refinement_source_text.trim()
        ? { refinement_source_text: request.metadata.refinement_source_text }
        : {}),
      ...(typeof request.metadata?.refinement_instruction === 'string' && request.metadata.refinement_instruction.trim()
        ? { refinement_instruction: request.metadata.refinement_instruction }
        : {}),
      ...(request.metadata?.extraction_scope && typeof request.metadata.extraction_scope === 'object'
        ? { extraction_scope: request.metadata.extraction_scope }
        : {}),
    }),
  })

  if (!response.ok) {
    throw new Error(await parseErrorDetail(response, 'Failed to detect Character index suggestions'))
  }

  throwIfCanceled(request.signal)

  const timestamp = nowIso()
  const payload = await response.json() as {
    chapter_id: string
    book_id?: string
    mode: 'detect-character-index'
    characters?: Array<{
      character_id: string
      kind?: CharacterIndexFigureKind
      display_name: string
      aliases?: string[]
      status?: CharacterLayerStatus
      origin?: CharacterLayerOrigin
      confidence?: number
      notes?: string
      metadata?: Record<string, any>
    }>
    occurrences?: Array<{
      occurrence_id: string
      character_id: string
      locator: {
        witnessId: string
        rowId: string
        localStart?: number
        localEnd?: number
      }
      role_labels?: string[]
      label?: string
      summary?: string
      status?: CharacterLayerStatus
      origin?: CharacterLayerOrigin
      confidence?: number
      notes?: string
      metadata?: Record<string, any>
    }>
    notes?: Array<{
      note_id: string
      anchor: any
      text: string
    }>
    diagnostics?: Record<string, any>
  }
  const preparedCall = request.metadata?.prepared_extraction_call
  const rawPackageExtraction = payload.diagnostics?.raw_extraction
  const focusedCharacterTarget = getFocusedCharacterTarget(request.metadata)
  const shouldAdaptPackageEvidence =
    request.metadata?.output_contract === PACKAGE_CHARACTER_EVIDENCE_OUTPUT_CONTRACT &&
    rawPackageExtraction &&
    preparedCall &&
    typeof preparedCall === 'object'
  const shouldAdaptPackageCoreferenceChains =
    request.metadata?.output_contract === PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT &&
    rawPackageExtraction &&
    preparedCall &&
    typeof preparedCall === 'object'
  const shouldAdaptPackageCoreferenceChainsV2 =
    request.metadata?.output_contract === PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT &&
    rawPackageExtraction &&
    preparedCall &&
    typeof preparedCall === 'object'
  const shouldAdaptPackageFocalizationCharacterization =
    request.metadata?.output_contract === PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT &&
    rawPackageExtraction &&
    preparedCall &&
    typeof preparedCall === 'object'

  if (shouldAdaptPackageEvidence) {
    const adapted = adaptPackageCharacterEvidenceToDetectorOutput({
      rawExtraction: rawPackageExtraction,
      preparedCall,
      chapterId: payload.chapter_id,
      createdAt: timestamp,
    })
    return {
      chapter_id: payload.chapter_id,
      ...(payload.book_id ? { book_id: payload.book_id } : {}),
      mode: payload.mode,
      characters: adapted.characters,
      occurrences: adapted.occurrences,
      notes: adapted.notes || [],
      diagnostics: {
        ...(payload.diagnostics || {}),
        ...(adapted.metadata || {}),
      },
    }
  }

  if (shouldAdaptPackageCoreferenceChains) {
    const adapted = adaptPackageCoreferenceChainsToDetectorOutput({
      rawExtraction: rawPackageExtraction,
      preparedCall,
      chapterId: payload.chapter_id,
      createdAt: timestamp,
    })
    const focusedAdapted = applyFocusedCharacterTarget(adapted, focusedCharacterTarget)
    return {
      chapter_id: payload.chapter_id,
      ...(payload.book_id ? { book_id: payload.book_id } : {}),
      mode: payload.mode,
      characters: focusedAdapted.characters,
      occurrences: focusedAdapted.occurrences,
      notes: focusedAdapted.notes || [],
      diagnostics: {
        ...(payload.diagnostics || {}),
        ...(focusedAdapted.metadata || {}),
      },
    }
  }

  if (shouldAdaptPackageCoreferenceChainsV2) {
    const adapted = adaptPackageCoreferenceChainV2ToDetectorOutput({
      rawExtraction: rawPackageExtraction,
      preparedCall,
      chapterId: payload.chapter_id,
      createdAt: timestamp,
    })
    return {
      chapter_id: payload.chapter_id,
      ...(payload.book_id ? { book_id: payload.book_id } : {}),
      mode: payload.mode,
      characters: adapted.characters,
      occurrences: adapted.occurrences,
      notes: adapted.notes || [],
      diagnostics: {
        ...(payload.diagnostics || {}),
        ...(adapted.metadata || {}),
      },
    }
  }

  if (shouldAdaptPackageFocalizationCharacterization) {
    const adapted = adaptPackageFocalizationCharacterizationToDetectorOutput({
      rawExtraction: rawPackageExtraction,
      preparedCall,
      existingLayer: request.existingLayer,
      createdAt: timestamp,
    })
    return {
      chapter_id: payload.chapter_id,
      ...(payload.book_id ? { book_id: payload.book_id } : {}),
      mode: payload.mode,
      characters: adapted.characters,
      occurrences: adapted.occurrences,
      notes: adapted.notes || [],
      diagnostics: {
        ...(payload.diagnostics || {}),
        ...(adapted.metadata || {}),
      },
    }
  }

  return {
    chapter_id: payload.chapter_id,
    ...(payload.book_id ? { book_id: payload.book_id } : {}),
    mode: payload.mode,
    characters: (payload.characters || []).map(character => ({
      character_id: character.character_id,
      ...(character.kind ? { kind: character.kind } : {}),
      display_name: character.display_name,
      aliases: character.aliases || [],
      status: character.status || 'auto',
      origin: character.origin || 'llm',
      ...(typeof character.confidence === 'number' ? { confidence: character.confidence } : {}),
      ...(character.notes ? { notes: character.notes } : {}),
      metadata: character.metadata || {},
      created_at: timestamp,
      updated_at: timestamp,
    })),
    occurrences: (payload.occurrences || []).map(occurrence => ({
      occurrence_id: occurrence.occurrence_id,
      character_id: occurrence.character_id,
      locator: occurrence.locator,
      role_labels: occurrence.role_labels || [],
      ...(occurrence.label ? { label: occurrence.label } : {}),
      ...(occurrence.summary ? { summary: occurrence.summary } : {}),
      status: occurrence.status || 'auto',
      origin: occurrence.origin || 'llm',
      ...(typeof occurrence.confidence === 'number' ? { confidence: occurrence.confidence } : {}),
      ...(occurrence.notes ? { notes: occurrence.notes } : {}),
      metadata: occurrence.metadata || {},
      created_at: timestamp,
      updated_at: timestamp,
    })),
    notes: (payload.notes || []).map(note => ({
      note_id: note.note_id,
      anchor: note.anchor,
      text: note.text,
      created_at: timestamp,
      updated_at: timestamp,
    })),
    diagnostics: payload.diagnostics || {},
  }
}

export const createWorkbenchCharacterIndexDetectorAdapter = (): CharacterLayerDetectorAdapter => ({
  id: 'llm-character-index',
  label: 'LLM resolver',
  kind: 'llm',
  detect: async request => {
    const result = await requestWorkbenchCharacterIndexDetection(request)
    return {
      characters: result.characters,
      occurrences: result.occurrences,
      notes: result.notes,
      metadata: {
        diagnostics: result.diagnostics,
      },
    }
  },
})

export const createWorkbenchCoreferenceChainDetectorAdapter = (): CharacterLayerDetectorAdapter => ({
  id: 'llm-coreference-chain',
  label: 'Coreference chains',
  kind: 'llm',
  detect: async request => {
    const outputContract = request.metadata?.output_contract === PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT
      ? PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT
      : PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT
    const windowCalls = getCoreferenceWindowCalls(request.metadata)
    if (windowCalls.length > 0) {
      const results: Array<{
        call: PreparedCoreferenceWindowCall
        result: WorkbenchCharacterIndexResult
      }> = []
      const failures: Array<Record<string, any>> = []
      for (const call of windowCalls) {
        throwIfCanceled(request.signal)
        try {
          results.push({
            call,
            result: await requestWorkbenchCharacterIndexDetection(buildCoreferenceWindowRequest(request, call, outputContract)),
          })
        } catch (error) {
          if (request.signal?.aborted || (error instanceof Error && error.name === 'AbortError')) {
            throw createCanceledError()
          }
          failures.push({
            window_id: call.window.windowId,
            window_index: call.window.windowIndex,
            row_label_range: call.window.rowLabelRange,
            source_row_ids: call.window.sourceRowIds,
            message: getErrorMessage(error),
          })
        }
      }
      if (results.length === 0) {
        const firstFailure = failures[0]
        throw new Error(
          firstFailure
            ? `Coreference window extraction failed for ${failures.length}/${windowCalls.length} windows: ${firstFailure.message}`
            : 'Coreference window extraction failed.'
        )
      }
      const result = mergeCoreferenceWindowResults({
        results,
        failures,
        windowCalls,
        outputContract,
      })
      return {
        characters: result.characters,
        occurrences: result.occurrences,
        notes: result.notes,
        metadata: {
          diagnostics: result.diagnostics,
        },
      }
    }
    const result = await requestWorkbenchCharacterIndexDetection(request)
    return {
      characters: result.characters,
      occurrences: result.occurrences,
      notes: result.notes,
      metadata: {
        diagnostics: result.diagnostics,
      },
    }
  },
})

export const createWorkbenchCoreferenceChainV2DetectorAdapter = (): CharacterLayerDetectorAdapter => ({
  id: 'llm-coreference-chain-v2',
  label: 'Coreference chains v2',
  kind: 'llm',
  detect: async request => createWorkbenchCoreferenceChainDetectorAdapter().detect({
    ...request,
    metadata: {
      ...(request.metadata || {}),
      output_contract: PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT,
    },
  }),
})

export const createWorkbenchFocalizationCharacterizationDetectorAdapter = (): CharacterLayerDetectorAdapter => ({
  id: 'llm-focalization-characterization',
  label: 'Focalization + characterization',
  kind: 'llm',
  detect: async request => {
    const result = await requestWorkbenchCharacterIndexDetection({
      ...request,
      metadata: {
        ...(request.metadata || {}),
        output_contract: PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT,
      },
    })
    return {
      characters: result.characters,
      occurrences: result.occurrences,
      notes: result.notes,
      metadata: {
        diagnostics: result.diagnostics,
      },
    }
  },
})
